import React, { Suspense } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes } from "react-router-dom";
import GigathlonHome from "./features/gigathlon/GigathlonHome";
import PilotShowSignupPage from "./features/gigathlon/PilotShowSignupPage";
import ValenciaPassportPage from "./features/gigathlon/ValenciaPassportPage";
import WhatsAppChatWidget from "./features/whatsapp-chat-widget/WhatsAppChatWidget";

const LoginPhonePage = React.lazy(() => import("./features/auth/LoginPhonePage"));
const ProfilePage = React.lazy(() => import("./features/gigathlon/ProfilePage"));
const JoinPassportPage = React.lazy(() => import("./features/gigathlon/JoinPassportPage"));
const PassportVerificationPage = React.lazy(() => import("./features/gigathlon/PassportVerificationPage"));
const ShowDetailsPage = React.lazy(() => import("./features/gigathlon/ShowDetailsPage"));
const OrganizerCheckInPage = React.lazy(() => import("./features/gigathlon/OrganizerCheckInPage"));
const VenueCheckInPage = React.lazy(() => import("./features/gigathlon/VenueCheckInPage"));
const AudienceStampPage = React.lazy(() => import("./features/gigathlon/AudienceStampPage"));
const ProductAnalyticsPage = React.lazy(() => import("./features/analytics/ProductAnalyticsPage"));
const ShowReviewPage = React.lazy(() => import("./features/gigathlon/ShowReviewPage"));
const ErrorPage = React.lazy(() => import("./features/gigathlon/ErrorPage"));

const RouteLoader = () => (
  <main style={{
    display: "grid",
    minHeight: "100vh",
    placeItems: "center",
    background: "#ffffff",
    color: "#20252f",
    fontWeight: 900,
  }}>
    Loading Gigathlon...
  </main>
);

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Gigathlon route failed:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="gigathlon-error-page">
          <section className="gigathlon-error-card" aria-labelledby="gigathlon-error-boundary-title">
            <Link to="/" className="gigathlon-wordmark">
              G<span className="gigathlon-wordmark__mic" aria-hidden="true">🎤</span>g<span>athlon</span>
            </Link>
            <p className="gigathlon-error-card__eyebrow">Something went wrong</p>
            <h1 id="gigathlon-error-boundary-title">We could not complete that step.</h1>
            <p>Please return home and try again.</p>
            <Link className="gigathlon-error-card__button" to="/">
              Back to home
            </Link>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

const App = () => (
  <BrowserRouter>
    <AppErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/" element={<GigathlonHome />} />
          <Route path="/comedypassport" element={<Navigate to="/valencia" replace />} />
          <Route path="/valencia" element={<ValenciaPassportPage />} />
          <Route path="/events/:showSlug/audience-qr" element={<VenueCheckInPage />} />
          <Route path="/venue/class-clowns-reunion/check-in" element={<Navigate to="/events/class-clowns-reunion/audience-qr" replace />} />
          <Route path="/mc/class-clowns-reunion/check-in" element={<OrganizerCheckInPage />} />
          <Route path="/admin/event-check-ins" element={<OrganizerCheckInPage />} />
          <Route path="/admin/event-check-ins/test" element={<OrganizerCheckInPage testMode />} />
          <Route path="/admin/product-analytics" element={<ProductAnalyticsPage />} />
          <Route path="/admin/show-reviews" element={<ShowReviewPage />} />
          <Route path="/shows/class-clowns-reunion" element={<PilotShowSignupPage />} />
          <Route path="/shows/:showSlug" element={<ShowDetailsPage />} />
          <Route path="/valencia-comedy-passport-pilot" element={<Navigate to="/valencia" replace />} />
          <Route path="/valencia-pitch" element={<Navigate to="/valencia" replace />} />
          <Route path="/login-phone" element={<LoginPhonePage />} />
          <Route path="/profile-setup" element={<Navigate to="/passport" replace />} />
          <Route path="/profile" element={<Navigate to="/passport" replace />} />
          <Route path="/passport" element={<ProfilePage />} />
          <Route path="/passport/join" element={<JoinPassportPage />} />
          <Route path="/passport/verify/:token" element={<PassportVerificationPage />} />
          <Route path="/stamp/:claimToken" element={<AudienceStampPage />} />
          <Route path="/error" element={<ErrorPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <WhatsAppChatWidget />
    </AppErrorBoundary>
  </BrowserRouter>
);

export default App;
